'use client';

import { Heart, Image as ImageIcon, Sparkles, Wand2 } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React from 'react';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysSpacesHeroProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  messages?: Record<string, unknown>;
}

/**
 * Allys-themed Hero section for the Royale Spaces feature.
 * Links to the portal-integrated Room Designer by default.
 */
export const AllysSpacesHero: React.FC<AllysSpacesHeroProps> = ({
  title,
  subtitle,
  ctaText,
  ctaLink,
  messages,
}) => {
  const pathname = usePathname();
  const spacesMsg = messages?.spaces as Record<string, unknown> | undefined;

  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = match?.[1] ?? 'en';
  const href = ctaLink || `/${locale}/account/spaces`;

  const displayTitle = title || safeGetString(spacesMsg?.heroTitle, 'Reimagine Your Space');
  const displaySubtitle = subtitle || safeGetString(spacesMsg?.heroSubtitle, 'Save a property you love, then let our AI transform its rooms into your dream interior. Choose a style, pick a palette, and see the result in seconds.');
  const displayCtaText = ctaText || safeGetString(spacesMsg?.startDesigning, 'Start Designing');

  return (
    <div className="relative bg-stone-950 text-white py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold-600/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-full mb-8">
          <Sparkles className="text-gold-500" size={16} />
          <span className="text-sm font-medium text-stone-300">AI-Powered Room Designer</span>
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 tracking-tight">
          <span className="bg-linear-to-r from-white via-gold-200 to-gold-500 bg-clip-text text-transparent">
            {displayTitle}
          </span>
        </h1>

        <p className="text-lg md:text-xl text-stone-400 max-w-3xl mx-auto mb-12 leading-relaxed">
          {displaySubtitle}
        </p>

        <Link
          href={href}
          className="inline-flex items-center gap-3 px-8 py-4 bg-linear-to-r from-gold-600 to-gold-400 text-stone-950 font-bold text-lg rounded-full shadow-[0_0_30px_rgba(204,165,41,0.3)] hover:shadow-[0_0_50px_rgba(204,165,41,0.5)] transition-all transform hover:scale-105"
        >
          <Wand2 size={20} />
          {displayCtaText}
        </Link>

        <div className="flex flex-wrap justify-center gap-4 mt-16">
          <div className="flex items-center gap-2 px-4 py-2 bg-stone-900/30 backdrop-blur-sm rounded-full border border-stone-800/50">
            <Heart size={16} className="text-gold-500" />
            <span className="text-sm text-stone-300">{safeGetString(spacesMsg?.featureUpload, 'Pick from Saved Properties')}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-stone-900/30 backdrop-blur-sm rounded-full border border-stone-800/50">
            <ImageIcon size={16} className="text-gold-500" />
            <span className="text-sm text-stone-300">{safeGetString(spacesMsg?.featureStyles, '16+ Design Styles')}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-stone-900/30 backdrop-blur-sm rounded-full border border-stone-800/50">
            <Sparkles size={16} className="text-gold-500" />
            <span className="text-sm text-stone-300">{safeGetString(spacesMsg?.featureInstant, 'Instant AI Results')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
